import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Sparkles, Landmark, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ModelPicker from "@/components/ModelPicker";

function emptyForm() {
  return { name: "", parent_company: "", hq_location: "", states_of_operation: [], political_affiliation: "", ownership_structure: "", key_executives: "", geopolitical_notes: "" };
}

export default function Owners() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [runningAI, setRunningAI] = useState(false);
  const [geoModel, setGeoModel] = useState("claude-sonnet-5");
  const params = useParams(); // support /owners/:id direct link
  const nav = useNavigate();

  const load = () => api.get("/owners").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.id) {
      api.get(`/owners/${params.id}`).then(({ data }) => setSelected(data)).catch(() => {});
    }
  }, [params.id]);

  const save = async () => {
    const payload = { ...form, states_of_operation: Array.isArray(form.states_of_operation) ? form.states_of_operation : String(form.states_of_operation || "").split(",").map(s => s.trim()).filter(Boolean) };
    try {
      if (editingId) await api.put(`/owners/${editingId}`, payload);
      else await api.post("/owners", payload);
      toast.success("Saved"); setOpen(false); setForm(emptyForm()); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const edit = (o) => {
    setEditingId(o.owner_id);
    setForm({ ...emptyForm(), ...o, states_of_operation: (o.states_of_operation || []).join(", ") });
    setOpen(true);
  };

  const del = async (id) => { if (!window.confirm("Delete owner?")) return; await api.delete(`/owners/${id}`); load(); };

  const runAI = async (id) => {
    setRunningAI(true);
    try {
      const { data } = await api.post(`/owners/${id}/geopolitical-analysis?model=${encodeURIComponent(geoModel)}`);
      toast.success("Analysis generated");
      const refreshed = await api.get(`/owners/${id}`);
      setSelected(refreshed.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI failed"); }
    finally { setRunningAI(false); }
  };

  const openView = (o) => { setSelected(o); nav(`/owners/${o.owner_id}`, { replace: true }); };
  const closeView = () => { setSelected(null); nav("/owners", { replace: true }); };

  const renderMd = (s) => {
    if (!s) return null;
    return s.split(/\n{2,}/).map((para, i) => {
      const html = para.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br/>");
      return <p key={i} className="text-sm text-slate-700 leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: html }} />;
    });
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="owners-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Ownership intel</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black">Corporate owners</h1>
          <p className="text-slate-500 mt-1 text-sm">Understand who runs the facilities — and the geopolitics behind them.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm()); setEditingId(null); } }}>
          <DialogTrigger asChild><Button data-testid="owner-new-btn" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" /> New owner</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Edit owner" : "New corporate owner"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label>Name</Label><Input data-testid="owner-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Parent company</Label><Input value={form.parent_company} onChange={e => setForm({ ...form, parent_company: e.target.value })} /></div>
              <div><Label>HQ Location</Label><Input value={form.hq_location} onChange={e => setForm({ ...form, hq_location: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>States of operation (comma-separated)</Label><Input value={form.states_of_operation} onChange={e => setForm({ ...form, states_of_operation: e.target.value })} /></div>
              <div><Label>Ownership structure</Label><Input placeholder="Private / Public / PE-backed" value={form.ownership_structure} onChange={e => setForm({ ...form, ownership_structure: e.target.value })} /></div>
              <div><Label>Political affiliation / lobbying</Label><Input value={form.political_affiliation} onChange={e => setForm({ ...form, political_affiliation: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Key executives</Label><Input value={form.key_executives} onChange={e => setForm({ ...form, key_executives: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Geopolitical notes</Label><Textarea rows={4} value={form.geopolitical_notes} onChange={e => setForm({ ...form, geopolitical_notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="owner-save" className="bg-slate-900 hover:bg-slate-800">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.length === 0 && <Card className="p-8 col-span-full text-center text-slate-400 border-slate-200 shadow-none">No owners yet.</Card>}
        {rows.map(o => (
          <Card key={o.owner_id} className="p-5 border-slate-200 shadow-none row-enter cursor-pointer hover:-translate-y-[2px] transition-transform duration-200" onClick={() => openView(o)} data-testid={`owner-row-${o.owner_id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2"><Landmark className="w-4 h-4 text-slate-500" /><div className="font-heading font-bold text-lg">{o.name}</div></div>
                <div className="text-xs text-slate-500 mt-1 font-mono-plex">{o.parent_company || "Independent"} · {o.hq_location || "HQ unknown"}</div>
              </div>
              {o.ai_risk_summary && <Badge className="bg-slate-900">AI briefed</Badge>}
            </div>
            <div className="flex flex-wrap gap-1 mt-3">
              {(o.states_of_operation || []).slice(0, 6).map(s => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && closeView()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selected && (<>
            <DialogHeader><DialogTitle>{selected.name}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-slate-500 uppercase tracking-widest font-mono-plex mb-1">Parent</div>{selected.parent_company || "—"}</div>
              <div><div className="text-xs text-slate-500 uppercase tracking-widest font-mono-plex mb-1">HQ</div>{selected.hq_location || "—"}</div>
              <div><div className="text-xs text-slate-500 uppercase tracking-widest font-mono-plex mb-1">Structure</div>{selected.ownership_structure || "—"}</div>
              <div><div className="text-xs text-slate-500 uppercase tracking-widest font-mono-plex mb-1">Political</div>{selected.political_affiliation || "—"}</div>
              <div className="md:col-span-2"><div className="text-xs text-slate-500 uppercase tracking-widest font-mono-plex mb-1">States</div>
                <div className="flex flex-wrap gap-1">{(selected.states_of_operation || []).map(s => <Badge key={s} variant="outline">{s}</Badge>)}</div>
              </div>
              <div className="md:col-span-2"><div className="text-xs text-slate-500 uppercase tracking-widest font-mono-plex mb-1">Executives</div>{selected.key_executives || "—"}</div>
              {selected.geopolitical_notes && <div className="md:col-span-2"><div className="text-xs text-slate-500 uppercase tracking-widest font-mono-plex mb-1">Notes</div><div className="whitespace-pre-wrap text-slate-700">{selected.geopolitical_notes}</div></div>}
            </div>

            <div className="mt-6 border-t border-slate-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Geopolitical brief</div>
                  <h4 className="font-heading font-bold text-lg">AI risk analysis</h4>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-44"><ModelPicker value={geoModel} onChange={setGeoModel} testId="geo-model" size="sm" /></div>
                  <Button onClick={() => runAI(selected.owner_id)} disabled={runningAI} data-testid="owner-ai-run" className="bg-slate-900 hover:bg-slate-800"><Sparkles className="w-4 h-4 mr-1" />{runningAI ? "Analyzing…" : selected.ai_risk_summary ? "Regenerate" : "Generate"}</Button>
                </div>
              </div>
              {selected.ai_risk_summary ? (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-4">{renderMd(selected.ai_risk_summary)}<div className="text-[10px] text-slate-400 font-mono-plex mt-2">Generated {selected.ai_summary_generated_at?.slice(0, 16).replace("T", " ")}</div></div>
              ) : (
                <div className="text-sm text-slate-400 italic">No AI brief yet. Click Generate to have Claude Sonnet 5 analyze the ownership & geopolitics.</div>
              )}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => del(selected.owner_id) & closeView()}><Trash2 className="w-4 h-4 text-rose-500 mr-1" /> Delete</Button>
              <Button variant="outline" onClick={() => { edit(selected); closeView(); }}>Edit</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
